import React, { useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";
import "../index.css";
import { useGetCategoryQuery, useGetSubCategoryQuery } from "../api/api";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../@/components/ui/accordion";
import SkeletonAccordionLoading from "../skeletonLoading/SkeletonAccordionLoading";
import ProductsListView from "../components/ProductsListView";
import { ScrollArea } from "../@/components/ui/scroll-area";
import { setAppState } from "../redux/slices/appState";
import { useDispatch } from "react-redux";
import { IsServiceTypeVendor, truncateString } from "../lib/utils";
import { MdOutlineMenu } from "react-icons/md";
import { HiOutlineX } from "react-icons/hi";

const Products: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(-1);
  const [selectedSubCategoryId, setSelectedSubCategoryId] =
    useState<number>(-1);
  const [openAccordion, setOpenAccordion] = useState<string>("");
  const isServiceTypeVendor = IsServiceTypeVendor();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const {
    data: categoriesData,
    isFetching: isFetchingCategories,
    isSuccess: categoriesSuccess,
  } = useGetCategoryQuery();
  const {
    data: subCategoriesData,
    isFetching: isFetchingSubCategories,
    isSuccess: subCategoriesSuccess,
  } = useGetSubCategoryQuery(
    {
      parentCategoryId: selectedCategoryId,
    },
    {
      refetchOnFocus: false,
    }
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSidebarOpen && window.innerWidth <= 1523) {
        const sidebar = document.getElementById("product-sidebar");
        const menuButton = document.getElementById("product-menu-button");

        if (
          sidebar &&
          menuButton &&
          !sidebar.contains(event.target as Node) &&
          !menuButton.contains(event.target as Node)
        ) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen]);

  const handleAccordionChange = (value: string) => {
    setOpenAccordion(openAccordion === value ? "" : value);
  };

  return (
    <div className="flex mb-1 pr-4 overflow-hidden bg-bgColor relative">
      <button
        id="product-menu-button"
        className="hidden max-[1523px]:block absolute top-4 left-4 z-10"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? <></> : <MdOutlineMenu className="w-6 h-6" />}
      </button>

      <div
        id="product-sidebar"
        className={`col-span-2 px-6 pb-6 pt-2 min-w-52 max-[1523px]:fixed max-[1523px]:left-0 max-[1523px]:top-0 max-[1523px]:bg-white max-[1523px]:h-screen max-[1523px]:overflow-y-auto max-[1523px]:z-50 max-[1523px]:shadow-lg ${
          isSidebarOpen
            ? "max-[1523px]:translate-x-0"
            : "max-[1523px]:-translate-x-full"
        } transition-transform duration-300 ease-in-out`}
      >
        <div className="flex justify-end">
          <button
            className="hidden max-[1523px]:block pl-2"
            onClick={() => setIsSidebarOpen(false)}
          >
            <HiOutlineX className="w-6 h-6 text-customGray" />
          </button>
        </div>

        <h1 className="text-xl font-medium leading-8">
          {isServiceTypeVendor ? "Service Catalog" : "Product Catalog"}
        </h1>
        <div className="mb-2 text-xs font-normal leading-5">
          {isServiceTypeVendor
            ? "Explore most popular service categories"
            : "Explore most popular product categories"}
        </div>
        <button
          className="w-full my-2 p-1 rounded bg-customGreenColor"
          onClick={() => {
            navigate(`/${isServiceTypeVendor ? "add-service" : "add-product"}`);
          }}
        >
          <div className="flex justify-center items-center text-white text-sm py-1">
            <FiPlus className="text-xl mr-1" />
            {isServiceTypeVendor ? <h1>Add Service</h1> : <h1>Add Product</h1>}
          </div>
        </button>
        {isFetchingCategories ? (
          <SkeletonAccordionLoading />
        ) : (
          <ScrollArea className="lg:h-[26rem] h-[22rem] w-full pr-2">
            <Accordion
              type="single"
              collapsible
              value={openAccordion}
              onValueChange={handleAccordionChange}
            >
              {categoriesSuccess &&
                categoriesData?.data?.map((category) => (
                  <AccordionItem value={category.name} key={category.id}>
                    <AccordionTrigger
                      className={`list-none m-0 py-2 ${
                        selectedCategoryId === category.id
                          ? "font-semibold text-black"
                          : ""
                      }`}
                      onClick={() => {
                        setSelectedSubCategoryId(-1);
                        setSelectedCategoryId(category.id);
                      }}
                    >
                      {truncateString(category.name, 15)}
                    </AccordionTrigger>
                    <AccordionContent>
                      {isFetchingSubCategories ? (
                        <SkeletonAccordionLoading />
                      ) : (
                        subCategoriesSuccess &&
                        subCategoriesData?.data?.map((subCategory) => (
                          <li
                            onClick={() => {
                              setSelectedSubCategoryId(subCategory.id);
                              if (window.innerWidth <= 1523) {
                                setIsSidebarOpen(false);
                              }
                            }}
                            className={`list-none m-0 py-1 pl-1 cursor-pointer mb-1
                              ${
                                selectedSubCategoryId === subCategory.id
                                  ? "font-semibold text-black"
                                  : ""
                              }`}
                            key={subCategory.id}
                          >
                            {subCategory.name}
                          </li>
                        ))
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
            </Accordion>
          </ScrollArea>
        )}
      </div>

      <div className={`w-full pt-2 overflow-hidden h-full max-[1523px]:ml-12`}>
        <ProductsListView
          categoryId={selectedCategoryId}
          subCategoryId={selectedSubCategoryId}
          setOpenAccordion={setOpenAccordion}
          setSelectedSubCategoryId={setSelectedSubCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
          openAccordion={openAccordion}
        />
      </div>
    </div>
  );
};

export default Products;
